import { CoralogixRum } from "@coralogix/browser";

class CoralogixLogger {
  private static instance: CoralogixLogger;

  private isInitialized = false;

  private constructor() {
    this.initializeCoralogix();
  }

  public static getInstance(): CoralogixLogger {
    if (!CoralogixLogger.instance) {
      CoralogixLogger.instance = new CoralogixLogger();
    }
    return CoralogixLogger.instance;
  }

  private initializeCoralogix(): void {
    try {
      const config = this.getConfig();

      if (!config.apiKey || !config.domain) {
        console.warn(
          "Coralogix configuration missing - logging will be disabled"
        );
        return;
      }

      CoralogixRum.init({
        public_key: config.apiKey,
        application: config.applicationName,
        coralogixDomain: config.domain as any,
        version: config.version,
        environment: config.environment,
        debug: config.environment === "development",
        instrumentations: {
          errors: true,
          fetch: true,
          xhr: true,
          custom: true,
          long_tasks: true,
          resources: true,
          interactions: true,
          web_vitals: true,
        },
      });

      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize Coralogix:", error);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private getConfig() {
    // eslint-disable-next-line no-underscore-dangle
    const env = window.__marketplace_frontend_env__ || {};

    return {
      applicationName: "marketplace-frontend",
      subsystemName: "frontend",
      apiKey: env.CORALOGIX_API_KEY || "",
      environment: env.REACT_APP_DEPLOYMENT_ENVIRONMENT || "development",
      domain: env.CORALOGIX_DOMAIN || "US1",
      version: this.getAppVersion(env),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  private getAppVersion(env: any): string {
    const gitHash =
      typeof CI_COMMIT_SHA !== "undefined"
        ? CI_COMMIT_SHA
        : env.REACT_APP_GIT_HASH;
    const buildNumber = env.BUILD_NUMBER || "unknown";

    if (gitHash && gitHash !== "undefined") {
      // Format: buildnumber-a1b2c3d
      return `${buildNumber}-${gitHash.substring(0, 7)}`;
    }

    return buildNumber;
  }

  public logError(
    message: string,
    error: unknown,
    context: Record<string, any> = {}
  ): void {
    if (!this.isInitialized) {
      console.error("Coralogix not initialized:", message, error, context);
      return;
    }

    try {
      const errorData = {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        ...context,
        timestamp: new Date().toISOString(),
        source: "error-boundary",
      };

      // Log the error with context
      CoralogixRum.error(message, errorData);

      // If it's an Error object, also capture the exception
      if (error instanceof Error) {
        CoralogixRum.captureError(error, {
          ...context,
          errorMessage: message,
        });
      }
    } catch (coralogixError) {
      console.error("Failed to log to Coralogix:", coralogixError);
      console.error("Original error:", message, error, context);
    }
  }

  public logInfo(message: string, context: Record<string, any> = {}): void {
    if (!this.isInitialized) {
      console.info("Coralogix not initialized:", message, context);
      return;
    }

    try {
      CoralogixRum.info(message, {
        ...context,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Failed to log info to Coralogix:", error);
    }
  }

  public logWarning(message: string, context: Record<string, any> = {}): void {
    if (!this.isInitialized) {
      console.warn("Coralogix not initialized:", message, context);
      return;
    }

    try {
      CoralogixRum.warn(message, {
        ...context,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Failed to log warning to Coralogix:", error);
    }
  }

  public addUserContext(userInfo: Record<string, any>): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      CoralogixRum.setUserContext({
        user_id: userInfo.id || userInfo.user_id || "unknown",
        user_name: userInfo.name || userInfo.user_name || "unknown",
        user_email: userInfo.email || userInfo.user_email,
        user_metadata: userInfo,
      });
    } catch (error) {
      console.error("Failed to set user context in Coralogix:", error);
    }
  }

  public addCustomAttribute(key: string, value: any): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      const labels = { [key]: value };
      CoralogixRum.setLabels(labels);
    } catch (error) {
      console.error("Failed to add custom attribute to Coralogix:", error);
    }
  }
}

export default CoralogixLogger.getInstance();
